import {
  accessTokenRootSelector,
  refreshTokenSelector,
} from '@data/selectors/authSelectors';
import { logout, updateAuthInfo } from '@data/slices/authSlice';
import { RootState } from '@data/store';
import { Store } from '@reduxjs/toolkit';
import { REFRESH_ACCESS_TOKEN_HEADER } from '@shared/constants';
import { isNetworkError } from '@shared/utils';
import { hasAccessTokenExpired } from '@utils/auth';
import axios, {
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { refreshAccessToken } from './auth';

const UNAUTHORIZED = 401;
let isReauthenticate = false;

interface AxiosConfig {
  _retry?: boolean;
}

type InternalAxiosRequestConfigExtended = InternalAxiosRequestConfig &
  AxiosConfig;

type AxiosRequestConfigExtended = AxiosRequestConfig & AxiosConfig;

export const requestInterceptor =
  (store: Store<RootState>) =>
  async (request: InternalAxiosRequestConfigExtended) => {
    const { dispatch, getState } = store;
    const state = getState();
    const { token } = accessTokenRootSelector(state) ?? {
      token: null,
    };

    const hasExpired = hasAccessTokenExpired();

    const isAuthenticateRequest = request.url?.includes('authenticate');
    const isRefreshRequest = request.headers?.[REFRESH_ACCESS_TOKEN_HEADER];

    if (request.headers?.['Content-Type'] !== 'multipart/form-data') {
      request.headers.setContentType('application/json');
    }

    if (isRefreshRequest) {
      isReauthenticate = true;
    }

    const shouldHaveSetHeaders = token && !hasExpired && !isAuthenticateRequest;

    const shouldRefresh =
      hasExpired && !isRefreshRequest && !isAuthenticateRequest;

    const shouldCancelRequest = isReauthenticate && !isRefreshRequest;

    // Cancels any new requests when reauthenticate.
    if (shouldCancelRequest) {
      request.cancelToken?.throwIfRequested();
    }

    // Add access token to the request header but only if the request is not a authenticate request.
    if (shouldHaveSetHeaders) {
      request.headers.setAuthorization(`Bearer ${token}`);

      return request;
    }

    // If the access token has expired, try to refresh it.
    if (shouldRefresh) {
      isReauthenticate = true;

      const refreshToken = refreshTokenSelector(state) ?? '';
      request._retry = true;
      if (refreshToken) {
        /**
         * Fetch new accessToken
         */
        const response = await refreshAccessToken(refreshToken);
        /**
         * Update information in redux store.
         */

        dispatch(updateAuthInfo(response));
        /**
         * Update the access token in the request header.
         * This will be used in the next request.
         * */

        request.headers.setAuthorization(`Bearer ${token}`);

        return request;
      }
    }

    return request;
  };

export const errorInterceptor =
  (store: Store<RootState>) => async (error: AxiosError) => {
    const originalRequest: AxiosRequestConfigExtended | undefined =
      error.config;
    const { dispatch } = store;

    const isRefreshRequest =
      originalRequest?.headers?.[REFRESH_ACCESS_TOKEN_HEADER];
    const networkError = isNetworkError(error.code);

    if (
      !networkError &&
      (isRefreshRequest || error.response?.status === UNAUTHORIZED)
    ) {
      dispatch(logout());
      isReauthenticate = false;
      return Promise.reject('Failed to refresh the access token. Logging out.');
    }

    return Promise.reject(error);
  };

export const initAxios = (store: Store<RootState>) => {
  axios.interceptors.request.use(requestInterceptor(store));
  axios.interceptors.response.use(response => {
    if (isReauthenticate) {
      isReauthenticate = false;
    }

    return response;
  }, errorInterceptor(store));
};
